/**
 * Table Content Component
 *
 * Orchestrates the table rendering with drag-and-drop context.
 */
import {
  DndContext,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import React from "react";
import { useDragAndDropLogic } from "../hooks/useDragAndDrop";
import { TableBody } from "./TableBody";
import { TableHeader } from "./TableHeader";
import { VirtualizedTableBody } from "./VirtualizedTableBody";
import type { AppTable } from "../features";
import type { TableBodyProps } from "../types";
import type { RowData } from "@tanstack/react-table";

type TableContentProps<T extends RowData> = {
  enableVirtualization: boolean
  tableStyle: () => React.CSSProperties
  table: AppTable<T>
  hasNoVisibleColumns: boolean
  enableSorting: boolean
  onChangeSort?: (field: string) => void
  enableColumnResizing: boolean
  enableColumnPinning: boolean
  enableColumnOrdering: boolean
  enableColumnFiltering: boolean
  tableBodyProps: TableBodyProps<T>
  data: T[]
};

export const TableContent = <T extends RowData>({
  enableVirtualization,
  tableStyle,
  table,
  hasNoVisibleColumns,
  enableSorting,
  onChangeSort,
  enableColumnResizing,
  enableColumnPinning,
  enableColumnOrdering,
  enableColumnFiltering,
  tableBodyProps,
  data,
}: TableContentProps<T>) => {
  const headerProps = {
    table,
    hasNoVisibleColumns,
    enableSorting,
    onChangeSort,
    enableColumnResizing,
    enableColumnPinning,
    enableColumnOrdering,
    enableColumnFiltering,
  };

  const dragAndDropLogic = useDragAndDropLogic(table);

  const wrapWithDragAndDrop = (children: React.ReactNode) => {
    if (!enableColumnOrdering) {
      return children;
    }

    return (
      <DndContext
        sensors={dragAndDropLogic.sensors}
        collisionDetection={closestCenter}
        onDragEnd={dragAndDropLogic.handleDragEnd}
      >
        <SortableContext items={dragAndDropLogic.columnIds} strategy={horizontalListSortingStrategy}>
          {children}
        </SortableContext>
      </DndContext>
    );
  };

  if (enableVirtualization) {
    const style = tableStyle();
    const header = <TableHeader {...headerProps} />;
    return (
      <div style={style}>
        {wrapWithDragAndDrop(
          <VirtualizedTableBody {...tableBodyProps} stickyHeader={header} />,
        )}
      </div>
    );
  }

  const BodyComponent = enableVirtualization && data.length > 100 ? VirtualizedTableBody : TableBody;

  return wrapWithDragAndDrop(
    <table className="min-w-full divide-y divide-gray-200" style={tableStyle()}>
      <TableHeader {...headerProps} />
      <BodyComponent {...tableBodyProps} />
    </table>,
  );
};
